import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import ScoreGauge from "@/components/ScoreGauge";
import PillarBar from "@/components/PillarBar";
import JurisdictionGrid from "@/components/JurisdictionGrid";
import Disclaimer from "@/components/Disclaimer";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { ArrowRight, AlertCircle, AlertTriangle, Info } from "lucide-react";

const PRIORITY_META = {
  CRITICAL: { color: "var(--rg-red)", Icon: AlertCircle },
  HIGH: { color: "var(--rg-amber)", Icon: AlertTriangle },
  MEDIUM: { color: "var(--rg-sage)", Icon: Info },
  LOW: { color: "var(--rg-muted)", Icon: Info },
};

export default function SharedReport() {
  const { reportId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/share/${reportId}`).then(({ data }) => setData(data)).catch((e) => setErr(e?.response?.data?.detail || "Report unavailable"));
  }, [reportId]);

  if (err) return <Layout><div className="max-w-3xl mx-auto px-6 py-20 text-center text-[var(--rg-text)]">{err}</div></Layout>;
  if (!data) return <Layout><div className="max-w-7xl mx-auto px-6 py-20 text-center text-[var(--rg-text)]">Loading shared report...</div></Layout>;

  const report = data.report;

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="rounded-md border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)] px-4 py-2.5 mb-4 text-center text-xs font-mono uppercase tracking-[0.2em] text-[var(--rg-sage)]" data-testid="watermark-header">
          Managed via Regulens Compliance Partner: <span className="text-[var(--rg-text-strong)]">{data.owner_name}</span>
        </div>
        <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-4 mb-6 flex items-start gap-3 text-sm text-[var(--rg-text)]" data-testid="shared-banner">
          <Info className="w-5 h-5 text-[var(--rg-sage)] flex-shrink-0" />
          <div>
            This report was generated for <span className="text-[var(--rg-text-strong)] font-mono">{report.store_url}</span> on {(report.audit_date || "").slice(0,10)}.
            This link expires on {data.expiry_date.slice(0,10)} ({data.view_count} views). It does not require a login.
          </div>
        </div>

        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl text-[var(--rg-text-strong)] mb-2">Compliance Report</h1>
        <div className="text-xs text-[var(--rg-muted)] font-mono mb-8">Data verified: {report.dataLastVerified} · Next review: {report.nextReviewDate}</div>

        <div className="grid lg:grid-cols-3 gap-6 mb-10">
          <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6 flex items-center justify-center">
            <ScoreGauge score={report.composite_score} testId="shared-gauge" />
          </div>
          <div className="lg:col-span-2 rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6 space-y-5">
            <PillarBar label="Sales Tax Nexus" score={report.pillar1} weight="35%" testId="shared-pillar-1" />
            <PillarBar label="Privacy Law Readiness" score={report.pillar2} weight="30%" testId="shared-pillar-2" />
            <PillarBar label="Policy Documents" score={report.pillar3} weight="20%" testId="shared-pillar-3" />
            <PillarBar label="Exposure Awareness" score={report.pillar4} weight="15%" testId="shared-pillar-4" />
          </div>
        </div>

        {report.executiveSummary && (
          <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6 mb-8">
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-2">Executive Summary</div>
            <p className="text-[var(--rg-text)] leading-relaxed">{report.executiveSummary}</p>
          </div>
        )}

        <h2 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] mb-5">Per-state findings & citations</h2>
        <JurisdictionGrid analysis={report.jurisdictionAnalysis || {}} verifiedSources={report.verifiedSources} testIdPrefix="shared-juris" />

        {(report.remediationItems || []).length > 0 && (
          <div className="mt-10">
            <h2 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] mb-5">Remediation plan</h2>
            <div className="space-y-3">
              {(report.remediationItems || []).map((item, i) => {
                const meta = PRIORITY_META[item.priority] || PRIORITY_META.LOW;
                return (
                  <div key={`${item.priority}-${item.jurisdiction}-${item.action}`} className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5 flex flex-col sm:flex-row gap-3 sm:items-start">
                    <div className="flex items-center gap-2 px-2.5 py-1 rounded-sm text-[11px] font-bold uppercase tracking-wide self-start" style={{ color: meta.color, borderColor: meta.color, borderWidth: 1, borderStyle: "solid", backgroundColor: `${meta.color}1a` }}>
                      <meta.Icon className="w-3.5 h-3.5" /> {item.priority}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[var(--rg-text-strong)] font-semibold break-words">{item.action}</div>
                      <div className="text-xs text-[var(--rg-muted)] font-mono mt-1 break-all">{item.jurisdiction} · {item.statuteCitation}</div>
                    </div>
                    <div className="text-[var(--rg-sage)] font-mono font-bold text-lg self-start">+{item.potentialGain}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-10 rounded-lg border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)] p-8 text-center">
          <h3 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] mb-2">Generate your own compliance report</h3>
          <Link to="/register"><Button size="lg" className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-bold">Start Free <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
        </div>

        <div className="mt-6 rounded-md border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)] px-4 py-2.5 text-center text-xs font-mono uppercase tracking-[0.2em] text-[var(--rg-sage)]" data-testid="watermark-footer">
          Managed via Regulens Compliance Partner: <span className="text-[var(--rg-text-strong)]">{data.owner_name}</span>
        </div>

        <div className="mt-6"><Disclaimer /></div>
      </section>
    </Layout>
  );
}
